import { useEffect, useRef } from "react";

/**
 * useLatestRef — retourne un `ref` dont `.current` est TOUJOURS la dernière
 * valeur (ou fonction) reçue à chaque render du composant appelant.
 *
 * Utile chaque fois qu'un listener global (visibilitychange, storage, SSE,
 * setInterval, WebSocket, IntersectionObserver, event bus custom, …) doit
 * appeler une version *courante* d'un `load` / `handler` / `state`, sans
 * quoi une closure figée capturée au premier render ferait perdre l'état
 * à jour (bug typique : « le refetch au retour de focus oublie le
 * paramètre de recherche »).
 *
 * Spec rev166 (2026-02) — hook extrait de la correction ad-hoc `loadRef`
 * appliquée dans `pages/Documents.jsx` (rev164). Voir
 * `memory/project_rules_spec.md` §« Pattern useLatestRef ».
 *
 * @template T
 * @param {T} value  Valeur / fonction dont on veut la dernière version.
 * @returns {{current: T}}  Ref stable, `.current` mis à jour à chaque render.
 */
export default function useLatestRef(value) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref;
}
